<template>
    <!-- Outer wrapper holds the panel's footprint in the parent's flex row and clips
         the slide-in transform so it doesn't extend past the panel's right edge
         (which would otherwise overflow the body and trigger a transient horizontal
         scrollbar — see panel-slide-fade rule in main.css). -->
    <!-- Width does all the spatial motion; content cross-fades (fade-in, opacity only) over the
         same 500ms. No translateX slide on the toggle — a horizontal slide on top of the width
         grow makes the panel outpace the sibling's shrink and visibly jerk.
         !min-w-0 neutralizes the host widthClass's min-w-[18rem] on this animating element — a
         min-width clamp makes the width snap straight to 18rem at the start of expand (looks like
         a pause/resume) instead of interpolating smoothly from the rail. The inner panel keeps
         min-w-[18rem] (clipped by overflow-hidden) so the text never compresses. -->
    <div class="h-full overflow-hidden flex !min-w-0 transition-[width,flex-basis] duration-500 ease-out"
         :class="collapsed ? 'flex-shrink-0 w-10 border-l' : widthClass">
    <!-- Collapsed rail: thin docked handle that reclaims horizontal space. Click to reopen. -->
    <transition name="fade-in" appear>
        <div v-if="collapsed" class="h-full w-full flex flex-col items-center p-1 border rounded-tl-md rounded-bl-md bg-neutral-100/90 dark:bg-neutral-900/80">
            <Popper hover arrow placement="left" content="Open AI Assist">
                <button class="bn-icon-only mt-2 w-auto h-auto" @click="toggleCollapsed">
                    <DlsAiBadge state="idle" class="w-6 flex-shrink-0" />
                </button>
            </Popper>
            <button class="bn-icon-only mt-2" @click="toggleCollapsed">
                <ChevronLeftIcon />
            </button>
        </div>
    </transition>
    <!-- Expanded panel. min-w-[18rem] floors the content width so it keeps its layout while the
         wrapper width animates — the panel slides/clips under the wrapper's overflow-hidden
         instead of reflowing and compressing the text frame-by-frame as it shrinks/grows. -->
    <transition name="fade-in" appear>
        <div v-if="!collapsed" class="h-full w-full min-w-[18rem] flex flex-col p-1">
            <!-- Header (fixed). Top-right warning icon appears whenever the global AI status
                 has flipped to degraded — gives users mid-flow context (in the panel they're
                 actively using) that retries may be slower or fail. -->
            <div class="w-full flex flex-row items-center flex-wrap px-1 py-2 border flex-shrink-0 rounded-tl-md bg-neutral-100/90 dark:bg-neutral-900/80">
                <div class="w-auto p-1 pl-2 mr-2">
                    <DlsAiLogo class="flex-shrink-0 w-16" />
                </div>
                <div class="flex-grow p-1">
                    <div class="w-full text-left p-1 font-semibold min-w-24 text-sm whitespace-normal line-clamp-2">{{ title }}</div>
                </div>
                <div v-if="!aiConnection" class="flex-shrink-0 p-1">
                    <Popper hover arrow placement="bottom" content="AI service is degraded — responses may be slower or fail. We'll retry automatically.">
                        <ExclamationCircleIcon class="h-5 w-5 text-amber" />
                    </Popper>
                </div>
                <div class="flex-shrink-0 p-1">
                    <Popper hover arrow placement="bottom" :content="collapseDisabled ? 'Finish or stop the current action to collapse' : 'Collapse AI Assist'">
                        <button class="bn-icon-only" :disabled="collapseDisabled" @click="toggleCollapsed">
                            <ChevronRightIcon />
                        </button>
                    </Popper>
                </div>
            </div>
            <!-- Scrollable middle: chat turns, action log, slot for output cards -->
            <div ref="scrollEl" class="w-full flex flex-col flex-grow flex-auto h-0 overflow-y-auto p-1 text-left border-x bg-neutral-100/90 dark:bg-neutral-900/80">
                <!-- Empty state -->
                <div v-if="!chatHistory.length && !actionLog.length" class="w-full flex flex-col justify-center p-2 py-4">
                    <div class="w-full p-1 flex items-center flex-wrap mb-1">
                        <DlsAiBadge state="working" class="w-7 mr-2 flex-shrink-0" />
                        <div class="font-semibold whitespace-normal">{{ emptyHeading }}</div>
                    </div>

                    <div v-if="emptyHint" class="px-2 text-left py-1 whitespace-pre-line break-words">{{ emptyHint }}</div>
                    <!-- Example prompts — plain italic text, default color, indented. -->
                    <ul v-if="emptyExamples.length" class="w-full flex flex-col space-y-0.5 px-8 py-1 text-left italic">
                        <li v-for="(ex, idx) in emptyExamples" :key="idx" class="whitespace-normal break-words">
                            '{{ typeof ex === 'string' ? ex : ex.label }}'
                        </li>
                    </ul>
                </div>
                <!-- Conversation timeline (turns + action outcomes interleaved chronologically) -->
                <template v-for="item in timeline" :key="item.kind === 'turn' ? `t-${item._idx}` : `a-${item._id}`">
                    <!-- History window boundary. Everything above is still readable here but is no
                        longer sent to the assistant, so it cannot refer back to it. -->
                    <div v-if="item.kind === 'window'" class="w-full flex flex-row items-center gap-2 px-2 py-1">
                        <div class="flex-grow border-t border-neutral-500/30"></div>
                        <div class="flex-shrink-0 opacity-60 whitespace-normal">Earlier messages aren't sent to the assistant</div>
                        <div class="flex-grow border-t border-neutral-500/30"></div>
                    </div>
                    <!-- Action outcome row — indented to align with the AI bubble (past the avatar)
                        so it visually nests under its originating turn. Actions on the latest turn
                        wait for the typewriter to finish (avoids spoiling the conclusion); they
                        render as nothing during the wait, never as a chat turn. -->
                    <div v-if="item.kind === 'action' && (item.turnIndex !== latestTurnIndex || latestTypingDone)" class="w-full flex flex-row items-center pl-9 pr-1 py-2">
                        <CheckCircleIcon v-if="item.accepted" class="h-4 w-4 text-green flex-shrink-0" />
                        <XCircleIcon v-else class="h-4 w-4 text-orange flex-shrink-0" />
                        <div class="flex-grow min-w-0 whitespace-normal line-clamp-2 text-left pl-1" :class="item.accepted ? 'text-green' : 'text-orange opacity-70'">
                            {{ item.label }}
                        </div>
                    </div>
                    <!-- Action waiting on the latest turn's typewriter — render nothing, not a ghost
                        chat turn. Without this branch the v-else below would treat the action item
                        as a turn (empty instruction → empty user bubble; empty summary → 'OK.'). -->
                    <template v-else-if="item.kind === 'action'"></template>
                    <!-- Chat turn: user (right, skipped for system/manual entries) + AI / system (left) -->
                    <template v-else>
                        <!-- User row — omitted for synthetic entries flagged isManual (no user prompt drove them) -->
                        <div v-if="!item.isManual" class="w-full flex flex-row justify-end items-start my-2">
                            <div class="rounded-xl rounded-tr-sm px-3 py-2 bg-neutral-200 text-neutral-900 dark:bg-neutral-700/50 dark:text-neutral-200 max-w-[80%] whitespace-pre-line text-left leading-tight break-words">
                                {{ item.instruction }}
                            </div>
                            <div class="h-8 w-8 rounded-full bg-sky-700/80 dark:bg-sky-200/20 flex items-center justify-center flex-shrink-0 text-white font-semibold ml-2">
                                {{ userInitials }}
                            </div>
                        </div>
                        <!-- AI / system row — text-only, no chat bubble. Color encodes state:
                            green for actioned ("Applied"), teal-600 for AI replies/questions,
                            neutral for manual system entries. -->
                        <div class="w-full flex flex-row justify-start items-start my-2">
                            <div class="pl-1 flex-shrink-0">
                                <DlsAiBadge state="idle" class="w-[20px] flex-shrink-0" />
                            </div>
                            <div
                                class="flex-grow min-w-0 pt-0.5 ml-3 mr-4 text-left leading-snug text-normal"
                            >
                                <div v-if="item.isManual" class="flex flex-row items-center text-normal font-semibold pb-1">
                                    <CheckCircleIcon class="h-4 w-4 mr-1 flex-shrink-0" />
                                    <div>{{ item.manualHeader || 'Manual action' }}</div>
                                </div>
                                <div v-else-if="item.wasActioned" class="pb-1">
                                    <span class="inline-block bg-lime-500 text-neutral-900 font-medium rounded-md px-2 py-0.5">✓ Applied</span>
                                </div>
                                <!-- Animate the typewriter only on a turn that's both currently latest AND
                                    hasn't already been typed once. After undo, returning-to-latest turns
                                    (already in typedTurnIndices) skip animation and render statically. -->
                                <TypePrompt
                                    v-if="!item.isManual && item._idx === latestTurnIndex && item.summary && !typedTurnIndices.includes(item._idx)"
                                    :key="`tp-${item._idx}`"
                                    :text="item.summary"
                                    :speed="18"
                                    :pauseBetweenSentences="120"
                                    textClass=""
                                    extraClass="whitespace-pre-line text-left break-words"
                                    @progress="scrollToBottom"
                                    @done="markTurnTyped(item._idx)"
                                />
                                <div v-else class="whitespace-pre-line text-left break-words">{{ item.summary || 'OK.' }}</div>
                                <div
                                    v-if="item.notes && (item._idx !== latestTurnIndex || latestTypingDone)"
                                    class="w-full whitespace-pre-line text-left pt-1"
                                >
                                    {{ item.notes }}
                                </div>
                                <!-- Per-turn undo: visible only while the matching frame is still in the stack
                                    AND the typewriter has finished (avoids the button sliding in mid-animation). -->
                                <div v-if="item.wasActioned && canUndoTo(item._idx) && (item._idx !== latestTurnIndex || latestTypingDone)" class="w-full flex justify-end pt-1">
                                    <button class="bn-text text-orange" @click="$emit('undo-to', item._idx)">
                                        <div>Undo to here</div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </template>
                </template>
                <!-- Domain-specific AI output cards (proposed rooms, companions, etc.).
                    typingDone is exposed so consumers can defer their cards until the
                    latest turn's typewriter has finished — prevents conclusion-spoiling. -->
                <slot :typingDone="!chatHistory.length || latestTypingDone"></slot>
                <!-- In-flight indicator. While retrying (server pushed an 'ai-retrying'
                    process_progress event for this socket), the caption switches to a
                    transient amber retrying message — the HTTP request itself is still in
                    flight, the panel just gives the user context for why it's taking longer. -->
                <div v-if="isApplying && !hideInflightIndicator" class="w-full flex flex-row items-center px-1 py-2">
                    <DlsAiBadge state="working" class="w-5 flex-shrink-0" />
                    <div v-if="retryingState" class="text-amber pt-0.5 ml-3">
                        AI service is busy — retrying (attempt {{ retryingState.attempt }}/{{ retryingState.maxRetries }})…
                    </div>
                    <div v-else class="text-teal-600 pt-0.5 ml-3">{{ progressMessage || 'Thinking…' }}</div>
                </div>
            </div>
            <!-- Footer: input + action buttons (fixed) -->
            <div class="w-full flex flex-col p-2 space-y-1 border-t rounded-bl-md flex-shrink-0 border bg-neutral-100/90 dark:bg-neutral-900/80 min-h-20 justify-center">
                <!-- Input row: textarea (auto-grows 1→2 lines, then scrolls) + send/stop button -->
                <div class="w-full flex flex-row items-center">
                    <textarea
                        ref="inputEl"
                        v-auto-resize
                        :value="localValue"
                        rows="1"
                        class="w-full flex-grow min-h-6 max-h-12 overflow-y-auto resize-none"
                        :placeholder="placeholder"
                        :disabled="inputDisabled"
                        @input="localValue = $event.target.value"
                        @keydown.enter.exact.prevent="onSubmit"
                    ></textarea>
                    <!-- Send when idle, Stop when in-flight. One slot, icon swaps. -->
                    <button
                        v-if="!isApplying"
                        class="min-h-0 border-0 rounded-full bg-teal-600 hover:bg-teal-500 text-white p-1 flex-shrink-0 ml-2"
                        :disabled="!canSubmit"
                        @click="onSubmit"
                    >
                        <ArrowUpIcon class="h-4 w-4" />
                    </button>
                    <button
                        v-else
                        class="min-h-0 border-0 rounded-full bg-teal-600 hover:bg-teal-500 text-white p-1 flex-shrink-0 ml-2"
                        @click="onStop"
                    >
                        <StopIcon class="h-4 w-4" />
                    </button>
                </div>
                <!-- Clear-chat row: own line below the input, left-aligned. Only shown
                    when there's a conversation worth clearing. -->
                <div v-if="chatHistory.length || actionLog.length" class="w-full flex flex-row items-center pb-0.5">
                    <button class="bn-text opacity-80" :disabled="isApplying" @click="$emit('clear')">
                        <div class="sm:hidden">Clear</div>
                        <div class="hidden sm:block">Clear Conversation</div>
                    </button>
                </div>
            </div>
        </div>
    </transition>
    </div>
</template>

<script>
import { ref, computed, inject, nextTick, onMounted, onUnmounted, watch } from 'vue'
import { CheckCircleIcon, XCircleIcon, ArrowUpIcon, StopIcon, ExclamationCircleIcon, ChevronRightIcon, ChevronLeftIcon } from '@heroicons/vue/solid'
import DlsAiBadge from '@/components/ai/DlsAiBadge.vue'
import DlsAiLogo from '@/components/ai/DlsAiLogo.vue'
import TypePrompt from '@/components/ui/TypePrompt.vue'
import autoResize from '@/components/ui/autoResize.js'

export default {
    components: {
        DlsAiBadge,
        DlsAiLogo,
        TypePrompt,
        CheckCircleIcon,
        XCircleIcon,
        ArrowUpIcon,
        StopIcon,
        ExclamationCircleIcon,
        ChevronRightIcon,
        ChevronLeftIcon
    },
    directives: {
        autoResize
    },
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        chatHistory: {
            type: Array,
            default: () => []
        },
        // How many recent turns the backend actually sends to the model (MAX_HISTORY_TURNS).
        // Older turns stay on screen but are no longer in the assistant's view — the timeline
        // marks the boundary so an invisible reset stops looking like forgetfulness.
        historyWindow: {
            type: Number,
            default: 6
        },
        // Action outcomes (confirm/skip on cards). Each: { kind: 'action', accepted: bool, label: string, turnIndex?: number }.
        // turnIndex (optional) lets the timeline interleave the action with the chat turn that produced it; without it, actions render after all turns.
        actionLog: {
            type: Array,
            default: () => []
        },
        title: {
            type: String,
            default: 'AI Assist'
        },
        widthClass: {
            type: String,
            default: 'w-96'
        },
        placeholder: {
            type: String,
            default: 'Tell me what to do…'
        },
        emptyHeading: {
            type: String,
            default: 'How can I help?'
        },
        emptyHint: {
            type: String,
            default: 'Tell me what you want to do and I\'ll take care of it.'
        },
        // Optional clickable example prompts shown below the hint. Each entry can be a
        // plain string (renders + fills the input verbatim on click) or { label, prompt }
        // (label shown to the user, prompt is what fills the input).
        emptyExamples: {
            type: Array,
            default: () => []
        },
        isApplying: {
            type: Boolean,
            default: false
        },
        // Predicate (turnIndex → boolean) used by the per-turn "Undo to here" buttons inside
        // each AI/manual bubble. Returns true iff the named turn still has a snapshot frame
        // available — older frames age out at the parent's stack cap and lose their button.
        canUndoTo: {
            type: Function,
            default: () => () => false
        },
        applyDisabled: {
            type: Boolean,
            default: false
        },
        // True while a companion suggestion card is showing — input stays disabled
        // until the user resolves every Yes/No so we don't accept a new instruction
        // mid-decision.
        awaitingResponse: {
            type: Boolean,
            default: false
        },
        // Suppress the in-flight "Thinking…" indicator. Set when the consumer is
        // rendering its own user-facing "waiting on you" state in the default slot
        // (e.g. a confirm-gate umbrella card). Doesn't affect isApplying gating —
        // input stays disabled, stop button stays visible — only the spinner row hides.
        hideInflightIndicator: {
            type: Boolean,
            default: false
        },
        // Live in-flight caption from the backend (process_progress beats, correlated by
        // parseId in useAiAssist). Falls back to "Thinking…" when empty. Superseded by the
        // amber retry caption when the request is mid-retry.
        progressMessage: {
            type: String,
            default: ''
        }
    },
    emits: ['update:modelValue', 'apply', 'undo-to', 'clear', 'stop'],
    setup(props, { emit }) {
        // Refs
        const global = inject('global', null)
        const scrollEl = ref(null)
        const inputEl = ref(null)
        // Live-typing buffer. The textarea binds to this local ref (not the modelValue prop)
        // so keystrokes never mutate the host's modelValue — which the host reads via
        // v-bind="ai.bind" and would otherwise re-render its whole page on every character
        // (e.g. a 138-room table). The value is lifted up to modelValue only at submit; the
        // watcher below syncs it back down when the composable clears/sets modelValue.
        const localValue = ref(props.modelValue)
        // Global AI provider health. Drives the amber header warning icon. Falls back to true
        // when injected global is missing (e.g. component used outside an auth context).
        const aiConnection = computed(() => global?.appStatus?.value?.aiConnection !== false)
        // Shared collapse preference. When true the card renders a thin rail and ignores the
        // host widthClass (see outer wrapper). State + persistence live in the global store
        // (mirrors scrollBarState — localStorage + auth/updateme + cross-tab socket sync).
        const collapsed = computed(() => global?.aiAssistCollapsedState?.value === true)
        // Block collapsing while a request is in flight or a companion card is awaiting the
        // user's response — collapsing mid-action would hide the live output / decision.
        const collapseDisabled = computed(() => props.isApplying || props.awaitingResponse)
        // Mid-request retrying state. Populated by the 'process_progress' window event when
        // stage === 'ai-retrying'. Cleared automatically when isApplying flips to false (the
        // request resolved — success or final-failure both clear the in-flight spinner).
        const retryingState = ref(null)
        const onProcessProgress = (e) => {
            const detail = e?.detail
            if (!detail || detail.stage !== 'ai-retrying') return
            retryingState.value = {
                attempt: Number(detail.attempt) || 0,
                maxRetries: Number(detail.maxRetries) || 0,
                reason: detail.reason || null
            }
        }
        // Tracks when the latest turn's typewriter animation has finished. Used to gate
        // rendering of the trailing notes block AND the slot's companion cards — without
        // this, both appear instantly while the summary is still typing, spoiling the
        // conclusion. Defaults to true (no typing in progress) so empty-summary turns
        // don't leave consumers waiting forever for a @done event that never fires.
        const latestTypingDone = ref(true)
        // Indices of turns that have already been typed once. Lets us suppress the
        // typewriter animation when undo brings a prior turn back to "latest" — re-typing
        // an already-seen message looks like a glitch. Append-only; stale indices for
        // dropped turns are harmless because we never return to those positions.
        const typedTurnIndices = ref([])
        const markTurnTyped = (idx) => {
            if (!typedTurnIndices.value.includes(idx)) typedTurnIndices.value.push(idx)
            latestTypingDone.value = true
        }

        // Computed
        // Pull initials from the authenticated user (firstName+lastName, or email fallback).
        // Falls back gracefully if injected global is missing (e.g. when used outside an auth context).
        const userInitials = computed(() => {
            const user = global?.authenticated?.value
            if (user?.firstName && user?.lastName) return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
            return user?.email?.[0]?.toUpperCase() || 'U'
        })
        // Index of the most recent chat turn — drives the typewriter animation: only the latest AI response animates.
        const latestTurnIndex = computed(() => props.chatHistory.length - 1)
        // Input is locked while the AI is mid-call OR the user owes a Yes/No on a
        // pending companion suggestion. Both block submission of a new instruction.
        const inputDisabled = computed(() => props.isApplying || props.awaitingResponse)
        // Send button needs a non-empty trimmed value to fire (when not in-flight).
        const canSubmit = computed(() => !inputDisabled.value && localValue.value.trim().length > 0)
        // Build a single chronologically-ordered timeline of chat turns + action outcomes.
        // Each turn carries _idx = its index in chatHistory so the template can detect "is this the latest?".
        const timeline = computed(() => {
            const out = []
            const actionsByTurn = new Map()
            const trailingActions = []
            for (const a of props.actionLog) {
                if (a == null) continue
                if (Number.isInteger(a.turnIndex)) {
                    if (!actionsByTurn.has(a.turnIndex)) actionsByTurn.set(a.turnIndex, [])
                    actionsByTurn.get(a.turnIndex).push(a)
                } else {
                    trailingActions.push(a)
                }
            }
            const firstSentIdx = Math.max(0, props.chatHistory.length - props.historyWindow)
            props.chatHistory.forEach((turn, i) => {
                if (i === firstSentIdx && firstSentIdx > 0) out.push({ kind: 'window', _id: 'window-boundary' })
                out.push({ kind: 'turn', _idx: i, ...turn })
                const acts = actionsByTurn.get(i)
                if (acts) for (const a of acts) out.push(a)
            })
            for (const a of trailingActions) out.push(a)
            return out
        })

        // Helpers
        const focusInput = () => {
            nextTick(() => {
                inputEl.value?.focus()
            })
        }
        const onSubmit = () => {
            if (!canSubmit.value) return
            // Lift the locally-buffered text into the host's modelValue synchronously, then
            // fire apply — the composable's onApply reads modelValue.value on the same tick.
            emit('update:modelValue', localValue.value)
            emit('apply')
        }
        const onStop = () => {
            emit('stop')
        }
        const toggleCollapsed = () => {
            // Guard the collapse direction only; expanding from the rail is always allowed.
            if (!collapsed.value && collapseDisabled.value) return
            global?.toggleAiAssistCollapsed?.()
        }

        // Watchers
        // Auto-scroll the conversation panel to the bottom whenever something gets appended:
        // a new chat turn, a new action-log entry, or the in-flight indicator appearing.
        // nextTick ensures the DOM has rendered the new content before we measure scrollHeight.
        const scrollToBottom = () => {
            nextTick(() => {
                if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight
            })
        }
        // Sync the local buffer down when the host changes modelValue out-of-band: cleared to
        // '' after a successful apply (useAiAssist onApply), or set programmatically by a
        // consumer. The guard skips our own submit-time lift, so it never echoes a keystroke.
        watch(() => props.modelValue, (v) => {
            if (v !== localValue.value) localValue.value = v
        })
        watch(() => props.chatHistory.length, scrollToBottom)
        watch(() => props.actionLog.length, scrollToBottom)
        watch(() => props.isApplying, scrollToBottom)
        // Retrying caption lives only for the duration of an in-flight call. When the request
        // resolves (success, final-failure, or user-stop), isApplying flips false — clear the
        // caption so the next call starts fresh without a stale 'attempt N/M' message.
        watch(() => props.isApplying, (applying) => {
            if (!applying) retryingState.value = null
        })
        // Prune typedTurnIndices when the history shrinks (collapse-on-action / undo /
        // clear). Without this, stale indices for dropped turns linger and a renumbered
        // turn that lands on a stale index would skip its typewriter animation.
        watch(() => props.chatHistory.length, (newLen, oldLen) => {
            if (newLen < oldLen) {
                typedTurnIndices.value = typedTurnIndices.value.filter(idx => idx < newLen)
            }
        })
        // Return the cursor to the input whenever the panel becomes "ready" — the AI
        // call finished AND every companion suggestion has been resolved. Lets the user
        // type their next instruction without clicking back into the textarea.
        watch(inputDisabled, (locked) => {
            if (!locked) focusInput()
        })
        // When a new turn arrives, reset the typing-done flag so trailing notes / slot
        // cards wait for the new typewriter to finish — but only when the new turn will
        // actually animate. Manual entries (synthetic split records) skip TypePrompt,
        // empty-summary turns don't render it, and turns we've already typed once
        // (e.g. after an undo brings them back to latest) skip too. All flip done=true
        // immediately so consumers don't wait for a @done event that will never fire.
        watch(latestTurnIndex, (newIdx) => {
            const turn = newIdx >= 0 ? props.chatHistory[newIdx] : null
            const hasSummary = typeof turn?.summary === 'string' && turn.summary.length > 0
            const isManual = !!turn?.isManual
            const alreadyTyped = typedTurnIndices.value.includes(newIdx)
            latestTypingDone.value = !hasSummary || isManual || alreadyTyped
        })

        // Lifecycle
        // Drop the cursor in the textarea on mount when the panel opens unblocked
        // (no in-flight call, no pending companion). Same idea as the watcher; this
        // covers the first paint where the watcher hasn't had a transition to fire on.
        // Also wire a MutationObserver on the scroll region so the panel re-pins to
        // the bottom on any DOM change inside it — undo, companion confirm/skip,
        // manual room create, slot card add/remove, and revealed-after-typing actions
        // all change content height without going through the existing length watchers.
        // requestAnimationFrame batches multiple per-frame mutations into one scroll.
        let scrollObserver = null
        let scrollFrame = null
        onMounted(() => {
            if (!inputDisabled.value) focusInput()
            if (scrollEl.value && typeof MutationObserver !== 'undefined') {
                scrollObserver = new MutationObserver(() => {
                    if (scrollFrame !== null) return
                    scrollFrame = requestAnimationFrame(() => {
                        scrollFrame = null
                        if (scrollEl.value) scrollEl.value.scrollTop = scrollEl.value.scrollHeight
                    })
                })
                scrollObserver.observe(scrollEl.value, {
                    childList: true,
                    subtree: true,
                    characterData: true
                })
            }
            // Listen for per-socket 'ai-retrying' nudges pushed by the backend's retry loop.
            // socketConnection.js dispatches process_progress on window for any per-socket
            // event; we filter by stage here so unrelated progress streams (BOM parse) don't
            // surface in this panel.
            window.addEventListener('process_progress', onProcessProgress)
        })
        onUnmounted(() => {
            scrollObserver?.disconnect()
            if (scrollFrame !== null) cancelAnimationFrame(scrollFrame)
            window.removeEventListener('process_progress', onProcessProgress)
        })

        return {
            scrollEl,
            inputEl,
            localValue,
            userInitials,
            latestTurnIndex,
            timeline,
            latestTypingDone,
            typedTurnIndices,
            markTurnTyped,
            inputDisabled,
            canSubmit,
            aiConnection,
            collapsed,
            collapseDisabled,
            retryingState,
            onSubmit,
            onStop,
            toggleCollapsed,
            scrollToBottom
        }
    }
}
</script>
